import type { GameListItem } from "../api";
import { genderLabel } from "../lib/gameFormat";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusBadge(g: GameListItem) {
  if (g.status === "live") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-600" /> Live
      </span>
    );
  }
  if (g.status === "completed") {
    const won = g.homeScore > g.awayScore;
    const tied = g.homeScore === g.awayScore;
    const label = tied ? "Tied" : won ? "Won" : "Lost";
    const cls = tied
      ? "bg-amber-100 text-amber-700"
      : won
      ? "bg-emerald-100 text-emerald-700"
      : "bg-rose-100 text-rose-700";
    return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
  }
  return (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
      Upcoming
    </span>
  );
}

export function GameCard({ game }: { game: GameListItem }) {
  const isCompleted = game.status === "completed";
  const gLabel = genderLabel(game.gender);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-brand-300 hover:shadow-md">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500">
            <span>{formatDate(game.date)}</span>
            {game.location && (
              <>
                <span>·</span>
                <span className="truncate">{game.location}</span>
              </>
            )}
            {(game.ageGroupCode || gLabel) && <span>·</span>}
            {game.ageGroupCode && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">
                {game.ageGroupCode}
              </span>
            )}
            {gLabel && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">
                {gLabel}
              </span>
            )}
          </div>
          <h3 className="mt-1 text-sm font-medium text-slate-900">
            vs {game.opponent ?? "TBD"}
          </h3>
          <div className="mt-2 flex items-center gap-2">
            {statusBadge(game)}
            {isCompleted && game.recordedBy && (
              <span className="text-xs text-slate-500">
                Synced by {game.recordedBy.name ?? game.recordedBy.email ?? "—"}
              </span>
            )}
          </div>
        </div>
        {isCompleted && (
          <div className="text-right">
            <div className="text-xl font-semibold tabular-nums text-slate-900">
              {game.homeScore}–{game.awayScore}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
